import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  Typography,
  Box,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { GROUP_BY_OPTIONS } from '../exportPcfConstants';
import type { ExportConfig, GroupBy } from '../types';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import {
  exportActionTooltips,
  exportFieldTooltips,
} from '../../../shared/config/actionHelpCatalog';
import SearchableDropdown, {
  type DropdownOption,
} from '../../../shared/ui/forms/SearchableDropdown';
import type { AvailableParameter } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import { getComponentColumnParameterName } from '../lib/componentColumns';

interface ExportSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  config: ExportConfig;
  onConfigChange: (updates: Partial<ExportConfig>) => void;
  onBrowseOutputPath?: () => void | Promise<void>;
  previewOnly?: boolean;
  contentRef?: React.RefObject<HTMLDivElement | null>;
  groupingRef?: React.Ref<HTMLElement>;
  outputPathRef?: React.Ref<HTMLElement>;
  validationRef?: React.Ref<HTMLElement>;
  availableParameters?: AvailableParameter[];
}

export default function ExportSettingsDialog({
  open,
  onClose,
  config,
  onConfigChange,
  onBrowseOutputPath,
  previewOnly = false,
  contentRef,
  groupingRef,
  outputPathRef,
  validationRef,
  availableParameters = [],
}: ExportSettingsDialogProps) {
  const theme = useTheme();
  const groupingParameterName = useConfigurationStore(
    (state) => state.menuConfiguration.groupingParameterName,
  );
  const groupingParameterSource = useConfigurationStore(
    (state) => state.menuConfiguration.groupingParameterSource,
  );
  const updateMenuConfiguration = useConfigurationStore(
    (state) => state.updateMenuConfiguration,
  );
  const groupingParameterOptions = React.useMemo<DropdownOption[]>(() => {
    const optionsByValue = new Map<string, DropdownOption>();

    availableParameters.forEach((parameter) => {
      const name = parameter.name.trim();
      const value = getComponentColumnParameterName({ label: name, revitParameter: name }) ?? name;
      if (!value || optionsByValue.has(value.toLowerCase())) {
        return;
      }

      optionsByValue.set(value.toLowerCase(), {
        value,
        label: name,
        description: [parameter.category, parameter.dataType].filter(Boolean).join(' · '),
      });
    });

    return Array.from(optionsByValue.values()).sort((left, right) =>
      left.label.localeCompare(right.label)
    );
  }, [availableParameters]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!previewOnly) {
          onClose();
        }
      }}
      maxWidth={false}
      fullWidth
      disableEscapeKeyDown={previewOnly}
      slotProps={{
        root: {
          disableEnforceFocus: previewOnly,
          disableAutoFocus: previewOnly,
          disableRestoreFocus: previewOnly,
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: '480px' },
          maxWidth: '480px',
          maxHeight: 'calc(100% - 72px)',
          m: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
          borderRadius: `${borderRadius.lg}px`,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ px: 2.25, py: 1.5, fontSize: '0.95rem', fontWeight: 600 }}>
        Export settings
      </DialogTitle>
      <DialogContent
        ref={contentRef}
        data-testid="export-settings-dialog-content"
        sx={{ px: 2.25, py: '4px !important' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 0.5 }}>
          {/* Group By */}
          <Box ref={groupingRef} data-testid="export-settings-tour-target-grouping">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Group systems by
              </Typography>
              <HelpTooltipTrigger
                label="Group systems by"
                help={exportFieldTooltips.groupBy}
                placement="bottom-start"
              />
            </Box>
            <FormControl fullWidth>
              <RadioGroup
                row
                aria-label="Grouping mode"
                value={config.groupBy}
                onChange={e => onConfigChange({ groupBy: e.target.value as GroupBy })}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  columnGap: 1,
                  rowGap: 0.25,
                  ...(previewOnly ? { pointerEvents: 'none', opacity: 0.85 } : {}),
                }}
              >
                {GROUP_BY_OPTIONS.map(opt => {
                  const optionLabel = (
                    <FormControlLabel
                      value={opt.value}
                      disabled={Boolean(opt.disabledReason)}
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">{opt.label}</Typography>
                      }
                      sx={{
                        m: 0,
                        minWidth: 0,
                        '& .MuiRadio-root': { p: 0.5, mr: 0.5 },
                        '& .MuiTypography-root': {
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          lineHeight: 1.2,
                        },
                      }}
                    />
                  );
                  // Disabled controls swallow pointer events, so the tooltip
                  // anchor must be an enabled wrapper around the radio.
                  return opt.disabledReason ? (
                    <Tooltip key={opt.value} title={opt.disabledReason} placement="top" arrow>
                      <Box
                        component="span"
                        data-testid={`grouping-option-${opt.value}`}
                        sx={{ display: 'inline-flex' }}
                      >
                        {optionLabel}
                      </Box>
                    </Tooltip>
                  ) : (
                    <React.Fragment key={opt.value}>{optionLabel}</React.Fragment>
                  );
                })}
              </RadioGroup>
            </FormControl>
            {config.groupBy === 'Parameter' && (
              <Box sx={{ mt: 1 }}>
                <Box
                  data-testid="export-settings-parameter-controls"
                  data-layout="responsive-inline"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'end',
                    gap: 1,
                    '@media (max-width: 360px)': {
                      gridTemplateColumns: 'minmax(0, 1fr)',
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: layoutGap.dense, display: 'block' }}
                    >
                      Parameter name
                    </Typography>
                    <SearchableDropdown
                      value={groupingParameterName}
                      onChange={(value) => updateMenuConfiguration({ groupingParameterName: value })}
                      options={groupingParameterOptions}
                      placeholder="Search project parameters"
                      ariaLabel="Grouping parameter"
                      freeSolo
                      disabled={previewOnly}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: `${borderRadius.md}px`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.6),
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, mb: layoutGap.dense, display: 'block' }}
                    >
                      Parameter source
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={groupingParameterSource}
                      onChange={(_event, next: 'Element' | 'Type' | null) => {
                        if (next) {
                          updateMenuConfiguration({ groupingParameterSource: next });
                        }
                      }}
                      disabled={previewOnly}
                    >
                      <ToggleButton value="Element">Element</ToggleButton>
                      <ToggleButton value="Type">Type</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.5 }}
                >
                  {groupingParameterName.trim()
                    ? `Grouping by ${groupingParameterName.trim()} (${groupingParameterSource.toLowerCase()}).`
                    : 'Choose a project parameter or enter its name.'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Output Path */}
          <Box ref={outputPathRef} data-testid="export-settings-tour-target-output-path">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Output path
              </Typography>
              <HelpTooltipTrigger
                label="Output path"
                help={exportFieldTooltips.outputPath}
                placement="bottom-start"
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Default output path..."
              value={config.outputPath}
              onChange={e => onConfigChange({ outputPath: e.target.value })}
              disabled={previewOnly}
              InputProps={{
                readOnly: previewOnly,
                endAdornment: (
                  <InputAdornment position="end">
                    <ActionHelpTooltip help={exportActionTooltips.browseOutputPath}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onBrowseOutputPath?.()}
                        aria-label="Browse output folder"
                        disabled={previewOnly}
                      >
                        <FolderOpenIcon fontSize="small" />
                      </IconButton>
                    </ActionHelpTooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: `${borderRadius.md}px`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                },
              }}
            />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: layoutGap.dense, display: 'block' }}>
              Empty uses the default project export path.
            </Typography>
          </Box>

          {/* Options */}
          <Box
            ref={validationRef}
            data-testid="export-settings-tour-target-validation"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Options</Typography>
              <HelpTooltipTrigger
                label="Export options"
                help={exportFieldTooltips.includeValidation}
                placement="bottom-start"
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.includeValidation}
                  onChange={e => onConfigChange({ includeValidation: e.target.checked })}
                  size="small"
                  disabled={previewOnly}
                />
              }
              label={
                <Typography variant="body2">Run validation before export</Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {/* Linked models */}
          <Box data-testid="export-settings-linked-models">
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Linked models</Typography>
                <HelpTooltipTrigger
                  label="Include linked models"
                  help={exportFieldTooltips.includeLinkedModels}
                  placement="bottom-start"
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.includeLinkedModels}
                    onChange={e => onConfigChange({ includeLinkedModels: e.target.checked })}
                    size="small"
                    disabled={previewOnly}
                  />
                }
                label={
                  <Typography variant="body2">Include linked models</Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, mt: layoutGap.dense, display: 'block' }}
            >
              Elements from linked Revit models are included only when this is enabled.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.25, py: 1.25 }}>
        <Button size="small" onClick={onClose} disabled={previewOnly}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
